import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { v4 as uuidv4 } from 'uuid';
import { StoreApi } from 'zustand';

import { ACCESS_TOKEN_STORAGE_KEY, clearSession, REFRESH_TOKEN_STORAGE_KEY } from '@app/constants/app/storage';
import { refresh } from './api';
import { issuesTokenPair, isSignInUrl, reloadsIntoSameRefresh } from './auth-urls';

import type { ApiStore } from './utils/types';

declare module 'axios' {
  interface AxiosRequestConfig {
    silentSuccess?: boolean;
  }
}
/* eslint-disable no-shadow */

enum APIMethod {
  POST = 'post',
  PATCH = 'patch',
  PUT = 'put',
  DELETE = 'delete',
  GET = 'get',
}
class HttpClient {
  private readonly axios: AxiosInstance;

  private readonly store: StoreApi<ApiStore>;

  public constructor(config: AxiosRequestConfig, store: StoreApi<ApiStore>) {
    this.axios = axios.create(config);
    this.store = store;
    this.axios.interceptors.response.use(this.successInterceptor, this.errorInterceptor);
    this.axios.interceptors.request.use(this.requestInterceptor);
  }

  isRefreshUrl = url => /\/auth\/refresh/.test(url);

  private requestInterceptor = async (config: InternalAxiosRequestConfig) => {
    const newConfig = { ...config };
    const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
    if (token) {
      const { exp }: { exp: number } = jwtDecode(token);
      const tokenHasExpired = new Date().getTime() / 1000 > Number(exp) - 5;
      if (tokenHasExpired && refreshToken && !this.isRefreshUrl(config.url)) {
        // after successful refreshing let's get new token and do request with updated token
        // check logic in the successInterceptor for refreshUrl
        const freshToken = await refresh(refreshToken).then(() => localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY));

        if (freshToken && newConfig.headers) {
          newConfig.headers['x-auth'] = `${freshToken}`;
          return newConfig;
        }

        // if sth went wrong we are canceling request
        return {
          ...config,
          cancelToken: new axios.CancelToken(cancel => cancel(`Cancel request ${config.url}`)),
        };
      }

      if (!tokenHasExpired && newConfig.headers) {
        newConfig.headers['x-auth'] = `${token}`;
      }
    }

    return newConfig;
  };

  private successInterceptor = (res: AxiosResponse) => {
    const {
      data,
      request,
      config: { method, silentSuccess },
    } = res;
    // `silentSuccess` is for a write the screen already shows as done, like a money tab's autosave.
    if (method !== APIMethod.GET && !silentSuccess && !this.isRefreshUrl(request.responseURL)) {
      this.store.setState({ success: { id: uuidv4(), message: 'Success!' } });
    }
    if (issuesTokenPair(request.responseURL)) {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, res.headers['x-auth']);
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, res.headers['x-refresh-token']);
    }

    if (this.isRefreshUrl(request.responseURL)) {
      // here we set new value to token
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, res.headers['x-auth']);
    }

    return data;
  };

  private errorInterceptor = (error): void => {
    if (error.response?.status === 401 && !isSignInUrl(error.config?.url)) {
      // Not `localStorage.clear()`: an expired session must not take this browser's greetings with it.
      clearSession();
      if (!reloadsIntoSameRefresh(error.config?.url, window.location.search)) window.location.reload();
    }

    if (this.isRefreshUrl(error.config?.url)) {
      this.store.setState({ refreshFailed: true });
    }
    if (error?.response?.data) {
      const { status } = error.response;
      // Callers can mark expected error statuses (e.g. 409 for the chapter dedup / in-use cases)
      // via `silentStatuses` on the request config — those skip the global error toast subscription
      // but still propagate the error so the local onError handler can branch on status.
      const silentStatuses: number[] = error.config?.silentStatuses ?? [];
      if (!silentStatuses.includes(status)) {
        this.store.setState({ error: { detail: error.response.data?.message || 'Error', status } });
      }
      // Propagate the response (status + data) on the thrown error so mutation onError
      // handlers can branch on HTTP status (e.g. 409 → duplicate-key / chapter-in-use UI).
      const wrapped: Error & { response?: typeof error.response; reason?: string } = new Error(
        error.response.data?.message || 'Error',
      );
      wrapped.response = error.response;
      // The API's own name for the rule it refused, kept apart from the sentence in `message`: a screen
      // branching on which refusal this was has to read a code, and `message` is prose that changes.
      wrapped.reason = error.response.data?.reason;
      throw wrapped;
    } else {
      this.store.setState({
        error: {
          detail: 'Connection error',
        },
      });
      throw new Error('No connection');
    }
  };

  get = (...args) => this.axios.get.apply(this, args);

  post = (...args) => this.axios.post.apply(this, args);

  patch = (...args) => this.axios.patch.apply(this, args);

  put = (...args) => this.axios.put.apply(this, args);

  delete = (...args) => this.axios.delete.apply(this, args);
}

export default HttpClient;
